import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language } from '@/i18n/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { preloadLanguage } from '@/i18n/app/loader';

interface LanguageContextType {
  language: Language;
  currentLanguage: Language; // Add alias for backward compatibility
  setLanguage: (language: Language) => void;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en'); // Default to English
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Initialize language on mount and when user changes
  useEffect(() => {
    let cancelled = false;

    // Commit the resolved language only AFTER its dictionary is loaded, so the
    // translation hook returns it synchronously on first render — no English
    // flash for returning FR/NL users. EN resolves instantly (statically bundled).
    const commitLanguage = async (lang: Language) => {
      try {
        await preloadLanguage(lang);
      } catch (error) {
        console.error('Failed to preload language dictionary, falling back to English:', error);
        lang = 'en';
      }
      if (cancelled) return;
      setLanguageState(lang);
      localStorage.setItem('preferred-language', lang);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lang;
      }
      setIsLoading(false);
    };

    const loadLanguage = async () => {
      if (user?.id) {
        // Try to load from database first
        const { data: preferences } = await supabase
          .from('user_preferences')
          .select('preferred_language')
          .eq('user_id', user.id)
          .maybeSingle();

        const dbLang = preferences?.preferred_language as Language;
        if (dbLang && ['en', 'nl', 'fr'].includes(dbLang)) {
          await commitLanguage(dbLang);
          return;
        }
      }

      // Fallback to localStorage
      const storedLang = localStorage.getItem('preferred-language') as Language;
      const initLanguage = (storedLang && ['en', 'nl', 'fr'].includes(storedLang)) ? storedLang : 'en';
      await commitLanguage(initLanguage);
    };

    loadLanguage();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Update language and persist to storage AND database
  const setLanguage = async (newLanguage: Language) => {
    // Load the target dictionary before swapping state so the UI flips straight
    // to the new language with no English flash. EN resolves instantly.
    try {
      await preloadLanguage(newLanguage);
    } catch (error) {
      console.error('Failed to load language dictionary:', error);
    }

    setLanguageState(newLanguage);
    localStorage.setItem('preferred-language', newLanguage);

    // Update document language attribute for accessibility
    if (typeof document !== 'undefined') {
      document.documentElement.lang = newLanguage;
    }

    // Sync to database if user is logged in
    if (user?.id) {
      try {
        await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            preferred_language: newLanguage
          }, {
            onConflict: 'user_id',
            ignoreDuplicates: false
          });
      } catch (error) {
        console.error('Failed to sync language preference to database:', error);
      }
    }
  };

  const contextValue: LanguageContextType = {
    language,
    currentLanguage: language, // Provide alias for backward compatibility
    setLanguage,
    isLoading
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};

// Helper hook for just getting translations (most common use case)
// This is kept for backward compatibility but not used with global content store
export const useTranslations = () => {
  const { currentLanguage } = useLanguage();
  // For now, return empty object - components should use global content store instead
  return {};
};